import { FunctionComponent, useState } from "react";

import {
  CheckboxField,
  DatePickerField,
  DefaultButton,
  Dialog,
  Heading,
  Stack,
  SubmissionForm,
  Text,
  TextInputField,
  TimePickerField,
  useFieldContext
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { useCustomerAccountQuery } from "@libs/api/gateways/cam/cam-gateway.hooks";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  AddStripeCustomerDto,
  Country,
  CountryText
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useAddStripeCustomer } from "@libs/api/gateways/plt/plt-gateway.hooks";
import { config } from "@libs/config/config";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

interface CreateStripeAccountDialogProps {
  tenantId: string;
  country: Country;
  crmId: string;
}

type CreateStripeFormValue = Omit<
  AddStripeCustomerDto,
  "tenantId" | "country" | "crmId"
>;

const validator = new Validator<CreateStripeFormValue>(validate => ({
  name: validate.string().required(),
  email: [validate.string().email(), validate.string().required()]
}));

export const CreateStripeAccountDialog: FunctionComponent<CreateStripeAccountDialogProps> = ({
  tenantId,
  country,
  crmId
}) => {
  const [hidden, setHidden] = useState<boolean>(true);
  const onDismiss = () => setHidden(true);
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    [Permissions.PltCustomerWrite, Permissions.PltSalesWrite],
    "or"
  );

  const { mutateAsync: createStripeCustomer } = useAddStripeCustomer();

  const { data: customerAccount } = useCustomerAccountQuery(crmId || "", true);
  const today = new Date();
  const timeValue = today.toLocaleTimeString(undefined, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit"
  });

  const isNonProd = config.env !== "prod";

  return (
    <>
      <DefaultButton
        disabled={!hasWritePermission}
        onClick={() => setHidden(false)}
        title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
      >
        Create Stripe Customer
      </DefaultButton>
      {!hidden && (
        <Dialog
          hidden={false}
          onDismiss={onDismiss}
          dialogContentProps={{
            title: "Create Stripe Customer",
            showCloseButton: true
          }}
        >
          <SubmissionForm<
            Omit<AddStripeCustomerDto, "tenantId" | "country" | "crmId">
          >
            initialValues={{
              name: customerAccount?.businessName || "",
              email: customerAccount?.email1 || "",
              addressLine1: customerAccount?.addressLine1 || "",
              addressLine2: customerAccount?.addressLine2 || "",
              postcode: customerAccount?.postcode || "",
              city: customerAccount?.city || "",
              state: customerAccount?.state || "",
              useTestClock: false,
              testClockDate: today,
              testClockTime: timeValue
            }}
            validate={validator.validate}
            // allowSubmitOnClean={customerAccount !== undefined}
            onSubmit={async values => {
              await createStripeCustomer({
                tenantId,
                crmId,
                name: values.name,
                email: values.email,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                postcode: values.postcode,
                city: values.city,
                state: values.state,
                country,
                useTestClock: values.useTestClock,
                testClockDate: values.testClockDate,
                testClockTime: values.testClockTime
              });
            }}
            cancelButtonProps={{ onClick: onDismiss }}
            submitButtonProps={{
              alwaysEnabled: !!customerAccount
            }}
          >
            {() => (
              <Stack tokens={{ childrenGap: 8 }}>
                <TextInputField name="name" label="Account name" required />
                <TextInputField name="email" label="Account email" required />

                <Stack tokens={{ childrenGap: 8 }}>
                  <Heading>Address</Heading>
                  <TextInputField name="addressLine1" placeholder="Line1" />
                  <TextInputField name="addressLine2" placeholder="Line2" />
                  <TextInputField name="city" placeholder="City" />
                  <TextInputField name="state" placeholder="State" />
                  <Stack
                    horizontal
                    tokens={{ childrenGap: 8 }}
                    verticalAlign="center"
                  >
                    <TextInputField name="postcode" placeholder="Postcode" />
                    <Text>{CountryText[country]}</Text>
                  </Stack>
                  {isNonProd && (
                    <Stack tokens={{ childrenGap: 8 }} verticalAlign="center">
                      <CheckboxField
                        name="useTestClock"
                        label="Use Test Clock"
                      />
                      <FormUseTestClockWatcher />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}
          </SubmissionForm>
        </Dialog>
      )}
    </>
  );
};

const FormUseTestClockWatcher: FunctionComponent = () => {
  const { value } = useFieldContext<boolean>("useTestClock");

  return !!value ? (
    <Stack>
      <Text variant="small">Enter a Date and time to use</Text>
      <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
        <DatePickerField name="testClockDate" />
        <TimePickerField name="testClockTime" />
      </Stack>
    </Stack>
  ) : null;
};
